// paths.ts — single source of truth for every server route.
//
// Three dicts split by concern; every server route is named in one of them:
//
//   1. PATHS — public, no-auth paths (auth bootstrap, health, self-capture).
//      PUBLIC_PATHS is derived from this dict; the auth gate in index.ts
//      uses that list to allow these endpoints through without requiring
//      JWT or token headers.
//
//   2. SDK_PATHS — the contract shipped in production game builds via the
//      Bugpunch SDK (Android / iOS / C# lanes). Renaming any of these
//      breaks already-shipped customers because their game binary has the
//      path baked in. Treat as immutable; deprecate via /api/v1 → /api/v2
//      versioning if the shape ever changes. The audit script in
//      `scripts/check-sdk-paths.cjs` catches drift between server,
//      SDK source, and web mirror.
//
//   3. DASHBOARD_PATHS — every other server route. Not contract-stable
//      (server + web ship together, so a rename is one coordinated edit);
//      having every path named gives a single file to scan for the full
//      API surface. Generated from a one-shot migration script — names are
//      mechanical (drop /api/, camelCase, mark :params with `by<Param>`).
//
// `buildPath(pattern, params)` at the bottom substitutes `:foo` segments
// with values; clients call it to construct concrete URLs from dynamic
// patterns.
//
// The web side has a hand-maintained verbatim mirror at
// `web/src/lib/paths.ts`. The audit script enforces parity.

// ---------------------------------------------------------------------------
// Public paths — no auth required. The gate in index.ts lets these through
// before checking req.user / token headers.
// ---------------------------------------------------------------------------

export const PATHS = {
  // Liveness probe.
  health: '/api/health',
  // Cheap public check that the in-container ingest sidecar is reachable.
  // The dashboard polls this to render a global "ingest down" banner so
  // operators see crash/exception/bug-report intake stalls immediately
  // instead of guessing from a quiet Issues page.
  healthIngestUp: '/api/health/ingest-up',

  // Public agent bootstrap/update endpoints. These mount before the SPA
  // fallback because they intentionally live outside /api for shell scripts.
  agentDownload: '/agent/download',
  agentSetupPs1: '/agent/setup.ps1',
  agentSetupSh: '/agent/setup.sh',
  agentVersion: '/agent/version',
  agentInstallPs1: '/agent/install.ps1',
  agentInstallSh: '/agent/install.sh',
  agentDownloadTarGz: '/agent/download.tar.gz',

  // Auth bootstrap — these run before the user has a JWT.
  authLogin: '/api/auth/login',
  authRegister: '/api/auth/register',
  authSetup: '/api/auth/setup',
  authNeedsSetup: '/api/auth/needs-setup',
  authTestLogin: '/api/auth/test-login',
  authValidateKey: '/api/auth/validate-key',
  v1AuthAppleCallback: '/api/v1/auth/apple/callback',
  // Invite acceptance — public (recipient has no JWT yet). Token in body is
  // the only authenticator.
  authInvitePreview: '/api/auth/invite-preview',
  authAcceptInvite: '/api/auth/accept-invite',
  // Self-service password reset — the user is signed out by definition. The
  // emailed token is the only authenticator for reset-password; forgot-password
  // always 200s so it leaks nothing.
  authForgotPassword: '/api/auth/forgot-password',
  authResetPassword: '/api/auth/reset-password',

  // Client-side error capture. Uses navigator.sendBeacon, which can't
  // carry an Authorization header — must be reachable without auth so
  // pre-login crashes still get reported.
  selfCapture: '/api/self-capture',

  // SDK self-diagnostic sink. The SDK posts here when its own code
  // throws (JNI binding failures, JSON parse, HTTP timeouts) — those
  // failures happen before the customer's project context is loaded,
  // so the endpoint cannot require an API key. Rate-limited.
  sdkError: '/api/sdk-error',

  // Agent emulator listing — read-only, public for the agent setup flow.
  agentsEmulators: '/api/agents/emulators',
  // Runner enrollment — swaps a single-use dashboard-minted code for an
  // agent token. The code in the body is the only authenticator.
  agentsEnroll: '/api/agents/enroll',

  // GitHub App webhook ingest — verifies X-Hub-Signature-256 against the
  // stored webhook secret, no JWT.
  githubWebhook: '/api/integrations/github/webhook',

  // Desktop app — latest release + Tauri auto-update endpoint. Public:
  // the dashboard's Download button fetches this without going through
  // /api/* auth (the desktop updater can't carry a JWT), and the data
  // returned is the same public GitHub release info anyone can see.
  desktopRelease: '/api/desktop/release',
  desktopUpdate: '/api/desktop/update',

  // Public marketing contact / enquiry form (landing page). No auth.
  contact: '/api/contact',
} as const;

export const PUBLIC_PATHS: readonly string[] = [
  PATHS.health,
  PATHS.healthIngestUp,
  PATHS.agentDownload,
  PATHS.agentSetupPs1,
  PATHS.agentSetupSh,
  PATHS.agentVersion,
  PATHS.agentInstallPs1,
  PATHS.agentInstallSh,
  PATHS.agentDownloadTarGz,
  PATHS.desktopRelease,
  PATHS.desktopUpdate,
  PATHS.authLogin,
  PATHS.authRegister,
  PATHS.authSetup,
  PATHS.authNeedsSetup,
  PATHS.authTestLogin,
  PATHS.authValidateKey,
  PATHS.authInvitePreview,
  PATHS.authAcceptInvite,
  PATHS.authForgotPassword,
  PATHS.authResetPassword,
  PATHS.selfCapture,
  PATHS.sdkError,
  PATHS.agentsEmulators,
  PATHS.agentsEnroll,
  PATHS.githubWebhook,
  PATHS.contact,
];

// Prefix-match public paths — used for endpoints with dynamic segments
// (e.g. /api/v1/share/issues/:token) where exact-match against PUBLIC_PATHS
// can't work. Each entry is matched as a startsWith() against the full
// request path. Keep this list short — broader prefixes mean more routes
// auto-granted public access.
export const PUBLIC_PATH_PREFIXES: readonly string[] = [
  // Public issue share — see DASHBOARD_PATHS.v1ShareIssuesByToken etc.
  // The route handlers themselves validate the token; the auth gate just
  // has to let unauthed requests through to reach them.
  '/api/v1/share/',
  // GitHub App webhook — verifies X-Hub-Signature-256 against the App's
  // webhook secret in the settings table.
  '/api/git/webhook',
];

// ---------------------------------------------------------------------------
// SDK-stable paths — the contract shipped to production games via the
// Bugpunch SDK. These cannot be renamed without breaking already-shipped
// customers; the only safe change is to add a new path (e.g. /api/v2/...).
//
// Sourced from:
//   server/sdk/android-src/bugpunch/src/main/java/au/com/oddgames/bugpunch/*.java
//   server/sdk/ios-src/Bugpunch/Sources/*.mm
//   server/sdk/csharp-src/Bugpunch/Sources/**/*.cs
//
// Kept as a flat dict so a `git log -L` on a single key surfaces the
// history if a rename ever needs to be planned.
// ---------------------------------------------------------------------------

export const SDK_PATHS = {
  // Bootstrap / health
  health: '/api/health',

  // Device lifecycle
  devicePoll: '/api/device-poll',
  devicePollScriptResult: '/api/device-poll/script-result',
  devicePollDebugDecision: '/api/device-poll/debug-decision',
  devicesRegister: '/api/devices/register',
  devicesReportTunnel: '/api/devices/report-tunnel',
  devicesIceServers: '/api/devices/ice-servers',

  // Directives + issues + sessions
  directiveResult: '/api/directives/:id/result',
  issuesIngest: '/api/issues/ingest',
  issueEventEnrich: '/api/issues/events/:id/enrich',
  sessionsUpload: '/api/sessions/upload',

  // Auth-gated static file route. The SDK fetches attachment thumbnails from
  // /api/files/<rel> with its API-key header, and mints short-lived signed
  // URLs via /api/files/sign before opening a clip in the OS player/browser
  // (which can't carry the header). Renaming this route breaks shipped SDKs.
  files: '/api/files',

  // Feedback
  feedback: '/api/feedback',
  // Single item fetch — the SDK uses this to open a feedback item's detail
  // directly from a `feedback_reply` push deep-link. GET is device/API-key
  // auth; PATCH/DELETE on the same URL stay QA-gated (see feedback.routes.ts).
  feedbackById: '/api/feedback/:id',
  feedbackComments: '/api/feedback/:id/comments',
  feedbackVote: '/api/feedback/:id/vote',
  feedbackAttachments: '/api/feedback/attachments',
  feedbackSimilarity: '/api/feedback/similarity',
  // Admin-curated tag definitions. GET is SDK-facing (the SDK fetches the
  // create-picker + filter list); the POST create shares the path but is
  // QA-gated. Registered before GET /api/feedback/:id in the route file.
  feedbackTags: '/api/feedback/tags',
  // Mark dashboard-author comments on this feedback item as read by the
  // SDK author (X-Device-Id). Mirrors v1ChatRead — clears the pill's
  // feature-icon badge instantly without waiting for the next /device-poll
  // tick to refold a zero count.
  v1FeedbackRead: '/api/v1/feedback/:id/read',

  // SDK-versioned (/api/v1/*) — the explicitly long-lived surface.
  v1AnalyticsEvents: '/api/v1/analytics/events',
  v1CoverageCatalog: '/api/v1/coverage/catalog',
  v1CoverageGoalCatalog: '/api/v1/coverage/goal-catalog',
  v1Config: '/api/v1/config',
  v1PerfEvents: '/api/v1/perf/events',
  v1LogSessionAppendCrash: '/api/v1/log-sessions/:sessionId/append-crash-tail',
  v1ProjectPinSigningSecret: '/api/v1/project/pin-signing-secret',

  // Editor-only — Bugpunch SDK Editor window calls /api/auth/validate-key
  // when the user pastes their server URL + API key, to confirm the key is
  // valid before saving. Same path is also in PATHS.authValidateKey for
  // the auth gate; tracked here separately because the SDK depends on it.
  authValidateKey: '/api/auth/validate-key',

  // SDK self-diagnostic sink (Android + iOS). Same path as PATHS.sdkError;
  // tracked here because the SDK depends on it (same pattern as authValidateKey).
  sdkError: '/api/sdk-error',

  // Email+password "Login with Bugpunch" from the C# profile picker. Same
  // path as PATHS.authLogin; tracked here because the SDK depends on it.
  authLogin: '/api/auth/login',

  // Chat
  v1ChatMessage: '/api/v1/chat/message',
  v1ChatMessages: '/api/v1/chat/messages',
  v1ChatThread: '/api/v1/chat/thread',
  v1ChatHours: '/api/v1/chat/hours',
  v1ChatRead: '/api/v1/chat/read',
  v1ChatUpload: '/api/v1/chat/upload',
  v1ChatRequestResult: '/api/v1/chat/request/result',
  v1ChatRequestAnswer: '/api/v1/chat/request/answer',
  // Player answers a "is this solved?" prompt (Agree / Disagree). agreed=false
  // reopens the thread server-side and notifies QA.
  v1ChatResolution: '/api/v1/chat/resolution',
  v1ChatReaction: '/api/v1/chat/reaction',
  // Player-typing indicator. SDK POSTs while the composer text-watcher
  // sees keystrokes (throttled ~1 Hz); QA dashboard GETs to drive the
  // ChatPanel/ChatThreadList "player is typing…" pip. Server TTL =
  // chatService.TYPING_TTL_MS.
  v1ChatTyping: '/api/v1/chat/typing',

  // Issues affecting the player (in-game "issues affecting you" surface).
  // The SDK lists issues this device produced events for that the team has
  // publicly commented on, opens a player-safe detail (basic description +
  // public comments only — opening marks it read), and posts a public reply.
  // Auth: API key + X-Device-Id (mirrors the chat SDK routes). The detail +
  // comment endpoints hard-gate on device-impact to prevent issue
  // enumeration. See issuesSdk.routes.ts + issuePlayerRepository.
  v1IssuesImpacting: '/api/v1/issues/impacting',
  v1IssuesByTypeId: '/api/v1/issues/:type/:id',
  v1IssuesByTypeIdComment: '/api/v1/issues/:type/:id/comment',
  // Single-file multipart image/video upload (field `file`) for a player
  // comment attachment. Returns { type, url, mime, size }; the URL is then
  // sent back in the media[] array on POST .../comment. Mirrors
  // feedbackAttachments. Category: issue-comment-sdk.
  v1IssuesCommentAttachments: '/api/v1/issues/comment-attachments',

  // NOTE: /profiles/top was removed (issue #309) — the SDK picker lists
  // saved profiles from its local cache and prunes locally-expired session
  // tokens on open, with no server round-trip. profiles/select is the SDK
  // contract: the picker POSTs the stored session token on selection.
  // Mirror of server.

  // SDK-side native Google + Apple sign-in. Body: { provider, idToken,
  // deviceId }. Server verifies the ID token against the mobile OAuth
  // client IDs (Google: android + ios; Apple: native iOS bundle id or
  // the dashboard Services ID for the Android CustomTabs web flow) and
  // returns the player identity { provider, sub, email, name, avatarUrl }.
  // Side-effect: UPSERT into device_authenticated_users with provider set
  // and user_id NULL (SSO identities are not Bugpunch staff) so the
  // picker's "recent identities on this device" list includes them.
  v1ProjectsByProjectIdProfilesSsoSignIn: '/api/v1/projects/:projectId/profiles/sso-sign-in',
  v1ProjectsByProjectIdProfilesForgotPassword:
    '/api/v1/projects/:projectId/profiles/forgot-password',
  // Token-validated identity switch (issue #309). SDK picker POSTs the stored
  // 120-day session token on selecting a saved profile; server re-validates +
  // slides the window. Body { sessionToken } → { ...identity, roleConfig,
  // sessionToken }; 401 on expiry. SDK contract.
  v1ProjectsByProjectIdProfilesSelect: '/api/v1/projects/:projectId/profiles/select',
  v1ProjectsByProjectIdAuthApplePrepare: '/api/v1/projects/:projectId/auth/apple/prepare',

  // SDK identity validation. Body: { provider, authId } — provider is
  // "bugpunch" | "google" | "apple"; authId is the user_id for the
  // bugpunch (internal) provider or the provider_sub for SSO.
  // Returns 200 { valid: true } when device_authenticated_users contains
  // a matching (device, project, provider, identity) row, or 200
  // { valid: false, reason: "identity_invalid" } when it does not.
  // Lets the SDK detect server-side identity revocation (admin deleted
  // the row, DB wiped, etc.) and clear its persisted identity so the
  // profile picker re-prompts on the next chat / feedback / debug-mode
  // entry. Auth: API key + X-Device-Token.
  v1ProjectsByProjectIdProfilesValidate: '/api/v1/projects/:projectId/profiles/validate',
  // Controlled-session seat join (editor seats launched with -bugpunchSession). Body: { join_token, device_id, device_name, seat_info }.
  v1ControlSessionsJoin: '/api/v1/control-sessions/join',
} as const;

// ---------------------------------------------------------------------------
// Dashboard paths — every other server route, named so AI assistants and
// human contributors can scan one file to see the full API surface. Drift
// risk is lower than for SDK_PATHS (the dashboard ships together with the
// server, so server↔web rename is a single coordinated edit), but having
// every path in one place wins for discoverability.
//
// Naming convention: drop /api/, camelCase the segments, mark :params with
// a `by<Param>` suffix (or embedded marker for non-trailing params). The
// migration script in this repo regenerates these names from URL structure
// so a rename is mechanical: change the URL string here, re-run the script,
// commit the result.
// ---------------------------------------------------------------------------

export const DASHBOARD_PATHS = {
  // Admin triage for public landing-page enquiries (see PATHS.contact).
  contactSubmissions: '/api/contact/submissions',
  contactSubmissionById: '/api/contact/submissions/:id',
  contactDestination: '/api/contact/destination',
  adminCleanupByTypeById: '/api/admin/cleanup/:type/:id',
  adminCleanupCandidates: '/api/admin/cleanup/candidates',
  adminCleanupRetention: '/api/admin/cleanup/retention',
  adminCredentialKey: '/api/admin/credential-key',
  adminCredentialKeyConsume: '/api/admin/credential-key/consume',
  adminCredentialKeyRotate: '/api/admin/credential-key/rotate',
  // librespeed runs against this prefix and appends /garbage / /empty /
  // /getIP — matches the upstream librespeed-js client convention.
  adminDiagnosticsLibrespeed: '/api/admin/diagnostics/librespeed',
  adminDiagnosticsLibrespeedEmpty: '/api/admin/diagnostics/librespeed/empty',
  adminDiagnosticsLibrespeedGarbage: '/api/admin/diagnostics/librespeed/garbage',
  adminDiagnosticsLibrespeedGetIP: '/api/admin/diagnostics/librespeed/getIP',
  adminErrors: '/api/admin/errors',
  adminHttpMetricsByRoute: '/api/admin/http-metrics/by-route',
  adminHttpMetricsBySource: '/api/admin/http-metrics/by-source',
  adminHttpMetricsSlow: '/api/admin/http-metrics/slow',
  adminHttpMetricsTimeseries: '/api/admin/http-metrics/timeseries',
  adminLogs: '/api/admin/logs',
  // Self-describing agent view of the server logs (admin-gated, NOT a public
  // token — server internals). Documents grep/lvl/since filters + offset paging
  // inline so an authed admin agent reads a bounded window, not the whole file.
  adminLogsMd: '/api/admin/logs.md',
  adminStorageHealth: '/api/admin/storage-health',
  adminStorageStats: '/api/admin/storage/stats',
  adminStorageCosts: '/api/admin/storage/costs',
  adminStorageReconcile: '/api/admin/storage/reconcile',
  adminStorageObject: '/api/admin/storage/object',
  adminStorageOrphans: '/api/admin/storage/orphans',
  adminStorageOrphansDelete: '/api/admin/storage/orphans/delete',
  adminStorageLocalOrphans: '/api/admin/storage/local-orphans',
  adminStorageLocalOrphansSweep: '/api/admin/storage/local-orphans/sweep',
  adminSystemStats: '/api/admin/system-stats',
  adminSystemStatsInspectStart: '/api/admin/system-stats/inspect/start',
  adminSystemStatsInspectStop: '/api/admin/system-stats/inspect/stop',
  adminSystemStatsInspectPing: '/api/admin/system-stats/inspect/ping',
  adminSystemStatsInspectData: '/api/admin/system-stats/inspect/data',
  adminUsage: '/api/admin/usage',
  adminUsageActive: '/api/admin/usage/active',
  adminUserTiers: '/api/admin/user-tiers',
  adminUserTiersById: '/api/admin/user-tiers/:id',
  agents: '/api/agents',
  // Agent-token build fetch — an agent races these to pick the fastest mirror.
  agentsBuildsByIdDownload: '/api/agents/builds/:id/download',
  agentsBuildsByIdMirrors: '/api/agents/builds/:id/mirrors',
  agentsByAgentIdAdbByDeviceIdByAction: '/api/agents/:agentId/adb/:deviceId/:action',
  agentsByAgentIdAdbByDeviceIdLogcat: '/api/agents/:agentId/adb/:deviceId/logcat',
  agentsByAgentIdAdbByDeviceIdScreenshot: '/api/agents/:agentId/adb/:deviceId/screenshot',
  agentsByAgentIdAdbByDeviceIdShell: '/api/agents/:agentId/adb/:deviceId/shell',
  agentsByAgentIdAdbByDeviceIdWebrtcClose: '/api/agents/:agentId/adb/:deviceId/webrtc-close',
  agentsByAgentIdAdbByDeviceIdWebrtcIce: '/api/agents/:agentId/adb/:deviceId/webrtc-ice',
  agentsByAgentIdAdbByDeviceIdWebrtcOffer: '/api/agents/:agentId/adb/:deviceId/webrtc-offer',
  agentsByAgentIdAvds: '/api/agents/:agentId/avds',
  agentsByAgentIdAvdsByAvdName: '/api/agents/:agentId/avds/:avdName',
  agentsByAgentIdAvdsByAvdNameByAction: '/api/agents/:agentId/avds/:avdName/:action',
  agentsByAgentIdCommand: '/api/agents/:agentId/command',
  agentsByAgentIdExec: '/api/agents/:agentId/exec',
  agentsByAgentIdLogs: '/api/agents/:agentId/logs',
  agentsByAgentIdOperations: '/api/agents/:agentId/operations',
  agentsByAgentIdStatus: '/api/agents/:agentId/status',
  agentsByAgentIdSystemImages: '/api/agents/:agentId/system-images',
  agentsByAgentIdRunnerSettings: '/api/agents/:agentId/runner-settings',
  agentsByAgentIdToolInstall: '/api/agents/:agentId/tool-install',
  agentsByAgentIdRunnerSecrets: '/api/agents/:agentId/runner-secrets',
  agentsByAgentIdLicenceReturn: '/api/agents/:agentId/licence-return',
  agentsByAgentIdUnityInstall: '/api/agents/:agentId/unity-install',
  agentsByAgentIdWorkspaceRefresh: '/api/agents/:agentId/workspace-refresh',
  agentsByAgentIdDisabled: '/api/agents/:agentId/disabled',
  agentsByAgentIdUpdateLog: '/api/agents/:agentId/update-log',
  agentsById: '/api/agents/:id',
  agentsEnrollmentCodes: '/api/agents/enrollment-codes',
  agentsHealth: '/api/agents/health',
  agentsHeartbeat: '/api/agents/heartbeat',
  agentsRegister: '/api/agents/register',
  analyticsCatalog: '/api/analytics/catalog',
  analyticsCustomKeys: '/api/analytics/custom-keys',
  analyticsFacets: '/api/analytics/facets',
  analyticsHealth: '/api/analytics/health',
  // Pipeline observability — admin-only. Aggregates the dashboard +
  // ingest processes' pipelineMetrics snapshots into one view: ingest
  // p50/p95/p99 latency, sqlite-busy retries, beautifier sidecar
  // restarts, S3 failure count, dedup hit rate. Single screen tells
  // you whether ingest is healthy.
  healthPipeline: '/api/health/pipeline',
  analyticsQuery: '/api/analytics/query',
  analyticsReports: '/api/analytics/reports',
  analyticsReportsById: '/api/analytics/reports/:id',
  analyticsBoards: '/api/analytics/boards',
  artifacts: '/api/artifacts',
  artifactsById: '/api/artifacts/:id',
  artifactsByIdResolve: '/api/artifacts/:id/resolve',
  artifactsByIdSources: '/api/artifacts/:id/sources',
  artifactsRegister: '/api/artifacts/register',
  appleSymbolsWanted: '/api/apple-symbols/wanted',
  appleSymbolsWantedBuilds: '/api/apple-symbols/wanted-builds',
  appleSymbolsFilterMissing: '/api/apple-symbols/filter-missing',
  appleSymbolsUnavailable: '/api/apple-symbols/unavailable',
  appleSymbolsSidecarByUuid: '/api/apple-symbols/sidecar/:uuid',
  authAdminResetPassword: '/api/auth/admin-reset-password',
  authChangePassword: '/api/auth/change-password',
  authMe: '/api/auth/me',
  authProfile: '/api/auth/profile',
  authVcsIdentities: '/api/auth/vcs-identities',
  // Avatar upload — multipart PNG (256×256, ~30 KB after client-side
  // crop). Stored at S3 `avatars/<userId>.png`; key written into
  // app_users.avatar_s3_key. Read back via presigned GET URLs in picker
  // rows; the raw streaming fallback below (v1ProfilesAvatarByUserId)
  // covers the case where presigned URLs have expired or are unreachable.
  authMeAvatar: '/api/users/me/avatar',
  // Raw avatar bytes — fallback streaming endpoint for the SDK avatar
  // cache when a presigned URL has expired or is unreachable (CORS / DNS
  // / etc). Streams the same S3 object the dashboard avatar uploader
  // writes to. Cached server-side with `Cache-Control: max-age=86400`.
  // Auth: API key + X-Device-Token. Server enforces tester_role==internal.
  // Lives in DASHBOARD_PATHS rather than SDK_PATHS because the current
  // SDK uses presigned URLs and never calls this directly.
  v1ProfilesAvatarByUserId: '/api/v1/profiles/avatar/:userId.png',
  // (profiles/select is an SDK contract in SDK_PATHS; /profiles/top was removed in #309.)
  // Cheap GET that older shipped SDK builds poll to drive the floating
  // chat unread badge. Current SDK derives unread from the chat thread
  // payload directly; kept here so legacy clients continue to work.
  v1ChatUnread: '/api/v1/chat/unread',
  authRefresh: '/api/auth/refresh',
  branches: '/api/branches',
  bugReport: '/api/bug-report',
  bugReportMediaByIdByFilename: '/api/bug-report-media/:id/:filename',
  builds: '/api/builds',
  buildsBulkDelete: '/api/builds/bulk-delete',
  buildsById: '/api/builds/:id',
  buildsByIdDownload: '/api/builds/:id/download',
  buildsByIdMirrors: '/api/builds/:id/mirrors',
  buildsByIdMirrorsByMirrorId: '/api/builds/:id/mirrors/:mirrorId',
  buildsByVersion: '/api/builds/by-version',
  buildsUpload: '/api/builds/upload',
  buildsSourceUpload: '/api/builds/source-upload',
  buildsSources: '/api/builds/sources',
  buildsSourceSnippet: '/api/builds/:buildHash/source',
  buildsAssemblyUpload: '/api/builds/assembly-upload',
  buildsAssemblies: '/api/builds/assemblies',
  buildsAssemblyBundle: '/api/builds/:buildHash/assemblies',
  coverageBuildsByProjectId: '/api/coverage/:projectId/builds',
  coverageChannelsByProjectId: '/api/coverage/:projectId/channels',
  coverageMergedHeatByProjectId: '/api/coverage/:projectId/heat',
  coverageByProjectIdByBuildVersion: '/api/coverage/:projectId/:buildVersion',
  coverageByProjectIdByBuildVersionByUnitType: '/api/coverage/:projectId/:buildVersion/:unitType',
  coverageAreasByProjectIdByBuildVersion: '/api/coverage/:projectId/:buildVersion/areas',
  coverageChurnByProjectIdByBuildVersion: '/api/coverage/:projectId/:buildVersion/churn',
  coverageHeatByProjectIdByBuildVersion: '/api/coverage/:projectId/:buildVersion/heat',
  coverageSurfaceButtonsByProjectIdByBuildVersion:
    '/api/coverage/:projectId/:buildVersion/surface-buttons',
  coverageFreshnessTimelineByProjectIdByBuildVersion:
    '/api/coverage/:projectId/:buildVersion/freshness-timeline',
  coverageGapsByProjectIdByBuildVersion: '/api/coverage/:projectId/:buildVersion/gaps',
  coverageLeaderboardByProjectIdByBuildVersion:
    '/api/coverage/:projectId/:buildVersion/leaderboard',
  coveragePresenceByProjectIdByBuildVersion: '/api/coverage/:projectId/:buildVersion/presence',
  coverageCatalogByProjectIdByBuildVersion: '/api/coverage/:projectId/:buildVersion/catalog',
  coverageContextKeysByProjectIdByBuildVersion:
    '/api/coverage/:projectId/:buildVersion/context-keys',
  coverageIssuesByProjectIdByBuildVersion: '/api/coverage/:projectId/:buildVersion/issues',
  goalsByProjectIdByBuildVersion: '/api/goals/:projectId/:buildVersion',
  goalsById: '/api/goals/:id',
  goalsByIdContributors: '/api/goals/:id/contributors',
  goalsByIdReset: '/api/goals/:id/reset',
  coverageEventVocabByProjectId: '/api/coverage/:projectId/event-vocab',
  coverageHelicopterByProjectId: '/api/coverage/:projectId/helicopter',
  coverageMinutesByProjectIdByBuildVersion: '/api/coverage/:projectId/:buildVersion/minutes',
  // Internal-tester-raised issues for the active build — feeds the side
  // rail on the QA Goals page so testers can drag them onto goals.
  coverageInternalIssuesByProjectIdByBuildVersion:
    '/api/coverage/:projectId/:buildVersion/internal-issues',
  // All issue ↔ goal links for the (project, build) — hydrates the
  // per-goal "linked issues" badge in one round-trip.
  coverageIssueGoalLinksByProjectIdByBuildVersion:
    '/api/coverage/:projectId/:buildVersion/issue-goal-links',
  // Per-goal link create/delete endpoints. Drop creates (idempotent),
  // delete removes a single link.
  coverageGoalIssueLinksByProjectIdByBuildVersionByGoalId:
    '/api/coverage/:projectId/:buildVersion/goals/:goalId/issues',
  coverageGoalIssueLinkByProjectIdByBuildVersionByGoalIdByIssueId:
    '/api/coverage/:projectId/:buildVersion/goals/:goalId/issues/:issueId',
  // All goals an issue is linked to — drives the "Linked to N goals"
  // section on the issue detail panel.
  coverageGoalsForIssueByIssueId: '/api/coverage/issues/:issueId/goals',
  chatAttachments: '/api/chat/attachments',
  chatTemplates: '/api/chat/templates',
  chatTemplatesById: '/api/chat/templates/:id',
  chatGifAttach: '/api/chat/gif/attach',
  chatGifSearch: '/api/chat/gif/search',
  chatMessagesByIdReaction: '/api/chat/messages/:id/reaction',
  chatThreads: '/api/chat/threads',
  chatThreadsOpen: '/api/chat/threads/open',
  chatThreadsByDeviceByDeviceId: '/api/chat/threads/by-device/:deviceId',
  chatThreadsById: '/api/chat/threads/:id',
  chatThreadsByIdAssign: '/api/chat/threads/:id/assign',
  chatThreadsByIdReply: '/api/chat/threads/:id/reply',
  chatThreadsByIdRequest: '/api/chat/threads/:id/request',
  chatThreadsByIdTakeControl: '/api/chat/threads/:id/take-control',
  chatThreadsByIdTyping: '/api/chat/threads/:id/typing',
  chatThreadsByIdUnread: '/api/chat/threads/:id/unread',
  chatThreadsByIdNotes: '/api/chat/threads/:id/notes',
  chatThreadsByIdNotesByNoteId: '/api/chat/threads/:id/notes/:noteId',
  chatPeople: '/api/chat/people',
  chatChannelAccounts: '/api/chat/channel-accounts',
  chatChannelAccountsById: '/api/chat/channel-accounts/:id',
  chatUnreadCount: '/api/chat/unread-count',
  chatMentions: '/api/chat/mentions',
  chatMentionsRead: '/api/chat/mentions/read',
  chatMentionsUnreadCount: '/api/chat/mentions/unread-count',
  crashesGroupsByIdSamplesBySampleId: '/api/crashes/groups/:id/samples/:sampleId',
  crashesGroupsByIdSamplesBySampleIdLogs: '/api/crashes/groups/:id/samples/:sampleId/logs',
  debugTools: '/api/debug-tools',
  debugToolsById: '/api/debug-tools/:id',
  debugToolsReorder: '/api/debug-tools/reorder',
  devices: '/api/devices',
  devicesByDeviceId: '/api/devices/:deviceId',
  devicesByDeviceIdControlClaim: '/api/devices/:deviceId/control/claim',
  devicesByDeviceIdControlHeartbeat: '/api/devices/:deviceId/control/heartbeat',
  devicesByDeviceIdControlRelease: '/api/devices/:deviceId/control/release',
  devicesByDeviceIdControlStatus: '/api/devices/:deviceId/control/status',
  devicesByDeviceIdProxy: '/api/devices/:deviceId/proxy/*',
  devicesByDeviceIdRequest: '/api/devices/:deviceId/request',
  devicesByDeviceIdRequestDebug: '/api/devices/:deviceId/request-debug',
  devicePollDebugBusy: '/api/device-poll/debug-busy',
  devicesByDeviceIdDiagnostics: '/api/devices/:deviceId/diagnostics',
  devicesByDeviceIdScreenshot: '/api/devices/:deviceId/screenshot',
  devicesByDeviceIdTags: '/api/devices/:deviceId/tags',
  devicesByDeviceIdTunnelStatus: '/api/devices/:deviceId/tunnel-status',
  devicesByDeviceIdTypedb: '/api/devices/:deviceId/typedb',
  devicesByIdDatabasesClose: '/api/devices/:id/databases/close',
  devicesByIdDatabasesDelete: '/api/devices/:id/databases/delete',
  devicesByIdDatabasesInsert: '/api/devices/:id/databases/insert',
  devicesByIdDatabasesOpen: '/api/devices/:id/databases/open',
  devicesByIdDatabasesProviders: '/api/devices/:id/databases/providers',
  devicesByIdDatabasesQuery: '/api/devices/:id/databases/query',
  devicesByIdDatabasesRaw: '/api/devices/:id/databases/raw',
  devicesByIdDatabasesScan: '/api/devices/:id/databases/scan',
  devicesByIdDatabasesSchema: '/api/devices/:id/databases/schema',
  devicesByIdDatabasesUpdate: '/api/devices/:id/databases/update',
  directives: '/api/directives',
  directivesById: '/api/directives/:id',
  emailInbound: '/api/email/inbound',
  facebookWebhook: '/api/facebook/webhook',
  facebookOauthStart: '/api/facebook/oauth/start',
  facebookOauthCallback: '/api/facebook/oauth/callback',
  feedbackCount: '/api/feedback/count',
  feedbackByIdCommentsByCommentId: '/api/feedback/:id/comments/:commentId',
  feedbackByIdConvertToTask: '/api/feedback/:id/convert-to-task',
  feedbackByIdMerge: '/api/feedback/:id/merge',
  feedbackByIdArchive: '/api/feedback/:id/archive',
  feedbackTagById: '/api/feedback/tags/:tagId',
  feedbackUnsubscribe: '/api/feedback/unsubscribe',
  filesSign: '/api/files/sign',
  issueDirectives: '/api/issue-directives',
  issueDirectivesById: '/api/issue-directives/:id',
  issueAttachmentRules: '/api/issue-attachment-rules',
  issueAttachmentRulesById: '/api/issue-attachment-rules/:id',
  issueRules: '/api/issue-rules',
  issueRulesCollection: '/api/issue-rules/collection',
  issueRulesCollectionById: '/api/issue-rules/collection/:id',
  issueRulesFingerprint: '/api/issue-rules/fingerprint',
  issueRulesFingerprintById: '/api/issue-rules/fingerprint/:id',
  issues: '/api/issues',
  issuesCounts: '/api/issues/counts',
  issuesFacets: '/api/issues/facets',
  issuesTestOrigins: '/api/issues/test-origins',
  issuesById: '/api/issues/:id',
  issuesByIdSamples: '/api/issues/:id/samples',
  issuesByIdSamplesBySampleIdDatabases: '/api/issues/:id/samples/:sampleId/databases',
  issuesByIdRequestSamples: '/api/issues/:id/request-samples',
  issuesByIdGithubOpen: '/api/issues/:id/github/open',
  issuesByIdIgnore: '/api/issues/:id/ignore',
  issuesByIdPaths: '/api/issues/:id/paths',
  issuesByIdCommonSteps: '/api/issues/:id/common-steps',
  issuesByIdGroupedContext: '/api/issues/:id/grouped-context',
  issuesByIdRelated: '/api/issues/:id/related',
  issuesByIdLinkedSessionCohort: '/api/issues/:id/linked-cohort',
  issuesByIdReopen: '/api/issues/:id/reopen',
  issuesByIdResolve: '/api/issues/:id/resolve',
  issuesByIdShare: '/api/issues/:id/share',
  feedbackByIdShare: '/api/feedback/:id/share',
  issuesByIdSnooze: '/api/issues/:id/snooze',
  issuesByIdUnsnooze: '/api/issues/:id/unsnooze',
  issuesByIdUnmerge: '/api/issues/:id/unmerge',
  issuesByIdRefingerprint: '/api/issues/:id/refingerprint',
  issuesByIdVote: '/api/issues/:id/vote',
  issuesByIdWatchers: '/api/issues/:id/watchers',
  issuesBulkPatch: '/api/issues/bulk-patch',
  issuesBulkDelete: '/api/issues/bulk-delete',
  issuesByTypeComments: '/api/issues/:type/:id/comments',
  issuesByTypeCommentsByCommentId: '/api/issues/:type/:id/comments/:commentId',
  issuesByTypeCommentsByCommentIdReactions: '/api/issues/:type/:id/comments/:commentId/reactions',
  issuesByTypeCommentsUploads: '/api/issues/:type/:id/comments/uploads',
  issuesEventsByIdBeautified: '/api/issues/events/:id/beautified',
  issuesEventsByIdEnrichDirective: '/api/issues/events/:id/enrich-directive',
  issuesEventsByIdRawPayload: '/api/issues/events/:id/raw-payload',
  issuesEventsByIdSymbolicate: '/api/issues/events/:id/symbolicate',
  issuesEventsByIdResolveFrames: '/api/issues/events/:id/resolve-frames',
  issuesMerge: '/api/issues/merge',
  issuesRecalculate: '/api/issues/recalculate',
  issuesRecomputeSamples: '/api/issues/recompute-samples',
  issuesRefingerprintProject: '/api/issues/refingerprint-project',
  issuesRefingerprintPreview: '/api/issues/refingerprint-preview',
  issuesRefingerprintBatch: '/api/issues/refingerprint-batch',
  issuesRegroup: '/api/issues/regroup',
  issuesSimilarity: '/api/issues/similarity',
  issuesSavedSearches: '/api/issues/saved-searches',
  issuesSavedSearchesById: '/api/issues/saved-searches/:id',
  issuesSavedSearchesDefault: '/api/issues/saved-searches/default',
  issueStatuses: '/api/issue-statuses',
  issueStatusesById: '/api/issue-statuses/:id',
  keysByKeyId: '/api/keys/:keyId',
  // Personal access tokens — account-scoped API keys managed from Settings →
  // Account → API Keys. Authenticate as the owner user on the read-only
  // /api/v1 REST surface (v1Projects/v1Issues/v1Tasks/v1Feedback below).
  usersMeApiKeys: '/api/users/me/api-keys',
  usersMeApiKeysByKeyId: '/api/users/me/api-keys/:keyId',
  llmGenerate: '/api/llm/generate',
  llmModelsByProvider: '/api/llm/models/:provider',
  llmTest: '/api/llm/test',
  notifications: '/api/notifications',
  notificationsMarkRead: '/api/notifications/mark-read',
  notificationsUnreadCount: '/api/notifications/unread-count',
  orgs: '/api/orgs',
  orgsByOrgId: '/api/orgs/:orgId',
  orgsByOrgIdMembers: '/api/orgs/:orgId/members',
  orgsByOrgIdMembersByMemberId: '/api/orgs/:orgId/members/:memberId',
  orgsByOrgIdBots: '/api/orgs/:orgId/bots',
  orgsByOrgIdBotsByBotId: '/api/orgs/:orgId/bots/:botId',
  orgsByOrgIdBotsByBotIdKeys: '/api/orgs/:orgId/bots/:botId/keys',
  orgsByOrgIdProjects: '/api/orgs/:orgId/projects',
  // Performance — dashboard-side enrichment lookups. Ingest path lives on
  // SDK_PATHS.v1PerfEvents; this is the matching read surface for the
  // Performance page (Problems panel calls it to label rows with each
  // device's reported screen size).
  performanceScreenSizes: '/api/performance/screen-sizes',
  // Server-requested 90s "performance samples" (perf_events carrying a
  // video_meta timeline). List by device for the Performance drilldown; detail
  // returns the full perf timeline for the PerfTimelineStrip viewer.
  performancePerfSamples: '/api/performance/perf-samples',
  performancePerfSampleById: '/api/performance/perf-samples/:id',
  // Per-project benchmark-sample anchor (capture conditions). GET reads,
  // PUT updates. Shipped to devices via gameConfig.perfSample at startup.
  performanceBenchmarkAnchor: '/api/performance/benchmark-anchor',
  // Compare mode: per-build_version recency + metric within a cohort (free
  // perf filter combo). Backs the Performance page "Compare" view, which pins
  // each cohort's current (newest) vs best (top-metric) build.
  performanceCohortBuilds: '/api/performance/cohort-builds',
  // Store-released versions (AppFigures ground truth) — config, sync, list.
  performanceStoreConfig: '/api/performance/store-config',
  performanceStoreReleases: '/api/performance/store-releases',
  performanceStoreReleasesSync: '/api/performance/store-releases/sync',
  performanceStrugglingDevices: '/api/performance/struggling-devices',
  projects: '/api/projects',
  projectsByProjectId: '/api/projects/:projectId',
  projectsByProjectIdIcon: '/api/projects/:projectId/icon',
  projectsByProjectIdKeys: '/api/projects/:projectId/keys',
  projectsByProjectIdCapabilities: '/api/projects/:projectId/capabilities',
  // Project-wide "pinned builds" — the chip strip on the QA Goals page.
  // Stored server-side (not per-user localStorage) so every dashboard
  // user on the same project sees the same set + can edit it. Mutations
  // broadcast on the `project-pins:<projectId>` dashboard event topic.
  projectsByProjectIdPinnedBuilds: '/api/projects/:projectId/pinned-builds',
  projectsByProjectIdPinnedBuildsByBuildVersion:
    '/api/projects/:projectId/pinned-builds/:buildVersion',
  // Dashboard Users page — signed-in testers/players for a project grouped
  // with the device(s) they've authenticated on (union of currently-
  // connected SDK devices + device_authenticated_users history). Read-only.
  projectsByProjectIdUsers: '/api/projects/:projectId/users',
  // Notification platform (issue #308) — OneSignal-style per-project push
  // credential wizard + enqueue. (The SDK-facing device push-token + ack
  // endpoints stay literal in pushNotifications.routes.ts until the native
  // lanes reference them, so they can move into SDK_PATHS without tripping
  // the "every SDK_PATHS entry is used by a lane" audit.)
  projectsByProjectIdPushCredentials: '/api/projects/:projectId/push-credentials',
  projectsByProjectIdPushCredentialsByPlatform:
    '/api/projects/:projectId/push-credentials/:platform',
  projectsByProjectIdPushCredentialsByPlatformTest:
    '/api/projects/:projectId/push-credentials/:platform/test',
  projectsByProjectIdNotifications: '/api/projects/:projectId/notifications',
  projectsByProjectIdLinkedSessions: '/api/projects/:projectId/linked-sessions',
  projectsByProjectIdLinkedSessionsById: '/api/projects/:projectId/linked-sessions/:msid',
  projectsByProjectIdLinkedSessionsByIdLogs: '/api/projects/:projectId/linked-sessions/:msid/logs',
  projectsByProjectIdLinkedSessionsByIdParticipantLogs:
    '/api/projects/:projectId/linked-sessions/:msid/participant-logs',
  projectsByProjectIdLinkedSessionsByIdClientSessions:
    '/api/projects/:projectId/linked-sessions/:msid/client-sessions',
  projectsByProjectIdLinkedSessionsByIdShare:
    '/api/projects/:projectId/linked-sessions/:msid/share',
  releases: '/api/releases',
  releasesBulkDelete: '/api/releases/bulk-delete',
  releasesById: '/api/releases/:id',
  releasesByIdBuilds: '/api/releases/:id/builds',
  releasesByIdDailyStats: '/api/releases/:id/daily-stats',
  releasesByIdNotes: '/api/releases/:id/notes',
  // Range endpoints — what changed between two build versions. Both must be
  // registered before `releasesById` or "/api/releases/:id" swallows them.
  releasesNotes: '/api/releases/notes',
  releasesSummary: '/api/releases/summary',
  releasesVersions: '/api/releases/versions',
  remoteConfigConditions: '/api/remote-config/conditions',
  remoteConfigConditionValues: '/api/remote-config/condition-values',
  remoteConfigConditionsById: '/api/remote-config/conditions/:id',
  remoteConfigConditionsReorder: '/api/remote-config/conditions/reorder',
  remoteConfigParameters: '/api/remote-config/parameters',
  remoteConfigParametersById: '/api/remote-config/parameters/:id',
  remoteConfigParametersByIdConditionsByConditionId:
    '/api/remote-config/parameters/:id/conditions/:conditionId',
  remoteConfigPublish: '/api/remote-config/publish',
  remoteConfigRollbackByVersion: '/api/remote-config/rollback/:version',
  remoteConfigVersions: '/api/remote-config/versions',
  remoteConfigVersionsByVersion: '/api/remote-config/versions/:version',
  runs: '/api/runs',
  runsByRunId: '/api/runs/:runId',
  runsByRunIdFinish: '/api/runs/:runId/finish',
  runsStart: '/api/runs/start',
  scriptsResults: '/api/scripts/results',
  scriptsResultsById: '/api/scripts/results/:id',
  scriptsResultsStats: '/api/scripts/results/stats',
  scriptsScheduled: '/api/scripts/scheduled',
  scriptsScheduledById: '/api/scripts/scheduled/:id',
  scriptsScheduledByIdMarkRun: '/api/scripts/scheduled/:id/mark-run',
  scriptsScheduledPending: '/api/scripts/scheduled/pending',
  scriptsSnippets: '/api/scripts/snippets',
  scriptsSnippetsById: '/api/scripts/snippets/:id',
  sessionProjects: '/api/session-projects',
  sessions: '/api/sessions',
  sessionsBulk: '/api/sessions/bulk',
  sessionsById: '/api/sessions/:id',
  sessionsByIdFiles: '/api/sessions/:id/files',
  sessionsByIdFilesByFileName: '/api/sessions/:id/files/:fileName',
  sessionsByIdSessionJson: '/api/sessions/:id/session.json',
  sessionsByIdVideo: '/api/sessions/:id/video',
  settingsChatHours: '/api/settings/chat-hours',
  settingsChatGif: '/api/settings/chat-gif',
  settingsBugReportAiTitle: '/api/settings/bug-report-ai-title',
  settingsCloudDevices: '/api/settings/cloud-devices',
  settingsFeedbackSimilarity: '/api/settings/feedback-similarity',
  settingsErrorReporter: '/api/settings/error-reporter',
  settingsGithub: '/api/settings/github',
  // GET (any authed) returns { enabled }; PUT (admin) toggles the
  // code-review feature. When off, /api/plastic/code-reviews/* returns 503
  // and the dashboard hides the page + IssueDetailPanel section.
  settingsCodeReview: '/api/settings/code-review',
  settingsLlm: '/api/settings/llm',
  settingsSlackWebhook: '/api/settings/slack-webhook',
  settingsDiscord: '/api/settings/discord',
  settingsFacebook: '/api/settings/facebook',
  // Global AppFigures personal access token (Settings → Integrations).
  settingsAppfigures: '/api/settings/appfigures',
  settingsAppfiguresProducts: '/api/settings/appfigures/products',
  snapshots: '/api/snapshots',
  snapshotsById: '/api/snapshots/:id',
  snapshotsByIdDownload: '/api/snapshots/:id/download',
  snapshotsByIdEntries: '/api/snapshots/:id/entries',
  snapshotsByIdEntry: '/api/snapshots/:id/entry',
  snapshotsByIdDiff: '/api/snapshots/:id/diff/:otherId',
  snapshotsByIdRestore: '/api/snapshots/:id/restore',
  snapshotsJobsByJobId: '/api/snapshots/jobs/:jobId',
  snapshotsUpload: '/api/snapshots/upload',
  stats: '/api/stats',
  storage: '/api/storage',
  symbols: '/api/symbols',
  symbolsByBuildId: '/api/symbols/:buildId',
  symbolsCheck: '/api/symbols/check',
  symbolsDeltaAbort: '/api/symbols/delta/abort',
  symbolsDeltaComplete: '/api/symbols/delta/complete',
  symbolsDeltaInit: '/api/symbols/delta/init',
  symbolsIl2cppMappingUpload: '/api/symbols/il2cpp-mapping/upload',
  symbolsIl2cppMappingUploadMulti: '/api/symbols/il2cpp-mapping/upload-multi',
  symbolsSidecarUpload: '/api/symbols/sidecar/upload',
  symbolsUpload: '/api/symbols/upload',
  symbolsUploadDirectAbort: '/api/symbols/upload-direct/abort',
  symbolsUploadDirectComplete: '/api/symbols/upload-direct/complete',
  symbolsUploadDirectInit: '/api/symbols/upload-direct/init',
  tasks: '/api/tasks',
  tasksById: '/api/tasks/:id',
  tasksByIdAttachments: '/api/tasks/:id/attachments',
  tasksByIdAttachmentsByAttId: '/api/tasks/:id/attachments/:attId',
  tasksByIdChangesets: '/api/tasks/:id/changesets',
  tasksByIdChangesetsByChId: '/api/tasks/:id/changesets/:chId',
  tasksByIdDocuments: '/api/tasks/:id/documents',
  tasksByIdDocumentsByDocId: '/api/tasks/:id/documents/:docId',
  tasksByIdBlockers: '/api/tasks/:id/blockers',
  tasksByIdBlocking: '/api/tasks/:id/blocking',
  tasksByIdCaused: '/api/tasks/:id/caused',
  tasksByIdCauses: '/api/tasks/:id/causes',
  tasksByIdDependencies: '/api/tasks/:id/dependencies',
  tasksByIdSubscribe: '/api/tasks/:id/subscribe',
  tasksByIdSubscription: '/api/tasks/:id/subscription',
  tasksDependenciesByDepId: '/api/tasks/dependencies/:depId',
  tasksMetaAssignees: '/api/tasks/meta/assignees',
  tasksNotifyRelease: '/api/tasks/notify-release',
  tasksTriageBadgeCount: '/api/tasks/triage-badge-count',
  tasksSavedSearches: '/api/tasks/saved-searches',
  tasksSavedSearchesById: '/api/tasks/saved-searches/:id',
  tasksSavedSearchesDefault: '/api/tasks/saved-searches/default',
  tasksStatuses: '/api/tasks/statuses',
  tasksStatusesById: '/api/tasks/statuses/:id',
  tasksFlags: '/api/tasks/flags',
  tasksFlagsById: '/api/tasks/flags/:id',
  tasksIncomingQuestions: '/api/tasks/incoming-questions',
  taskWorkflowRules: '/api/task-workflow-rules',
  typedb: '/api/typedb',
  typedbById: '/api/typedb/:id',
  typedbMeta: '/api/typedb/meta',
  typedbUpload: '/api/typedb/upload',
  typedbVersions: '/api/typedb/versions',
  v1ConfigBySection: '/api/v1/config/:section',
  v1ConfigEdit: '/api/v1/config/edit',
  // ── Public REST API (read-only) ────────────────────────────────────────
  // Issue sign-off writes (write/admin-scoped personal token or signed-in user).
  // A two-segment GET under :id first hits the SDK's GET /api/v1/issues/:type/:id,
  // which mounts first and next()s any :type that isn't a player issue type —
  // that is what lets v1IssuesByIdLogs through.
  v1IssuesByIdComments: '/api/v1/issues/:id/comments',
  v1IssuesByIdChangesets: '/api/v1/issues/:id/changesets',
  v1IssuesByIdChangesetsByLinkId: '/api/v1/issues/:id/changesets/:linkId',
  v1IssuesByIdResolve: '/api/v1/issues/:id/resolve',
  v1IssuesByIdMerge: '/api/v1/issues/:id/merge',
  v1IssuesByIdUnmerge: '/api/v1/issues/:id/unmerge',
  v1IssuesByIdLogs: '/api/v1/issues/:id/logs.txt',
  v1IssuesByIdLogsBare: '/api/v1/issues/:id/logs',
  v1ProjectsByIdStatuses: '/api/v1/projects/:id/statuses',
  v1ProjectsByIdSymbolicate: '/api/v1/projects/:id/symbolicate',
  // Documented at /docs/rest-api. Auth: personal access token (Settings →
  // Account → API Keys), project API key, or dashboard JWT. Served by
  // apiV1.routes.ts, which mounts AFTER issuesSdk so v1IssuesById (:id, one
  // segment) can't capture the SDK's /api/v1/issues/impacting.
  v1Projects: '/api/v1/projects',
  v1Issues: '/api/v1/issues',
  v1IssuesById: '/api/v1/issues/:id',
  v1Tasks: '/api/v1/tasks',
  v1TasksById: '/api/v1/tasks/:id',
  v1Feedback: '/api/v1/feedback',
  v1FeedbackById: '/api/v1/feedback/:id',
  v1Releases: '/api/v1/releases',
  v1ReleaseNotes: '/api/v1/releases/notes',
  // Personal-token identity + device control (apiV1Devices.routes.ts).
  // /devices lists INTERNAL-role devices only; /proxy/* forwards Remote IDE
  // RPC to one of them and needs an admin-scoped personal token.
  v1Me: '/api/v1/me',
  v1Devices: '/api/v1/devices',
  v1DevicesByDeviceIdProxy: '/api/v1/devices/:deviceId/proxy/*',
  // Public issue share — read-only views of an issue keyed on the issue id.
  // Access is gated by the issue's `public_share_until` column (24h window
  // opened via POST /api/issues/:id/share). Allowlisted via the
  // /api/v1/share/ prefix so the auth gate lets them through.
  v1ShareIssuesById: '/api/v1/share/issues/:id',
  v1ShareIssuesByIdMd: '/api/v1/share/issues/:id.md',
  v1ShareFeedbackById: '/api/v1/share/feedback/:id',
  v1ShareFeedbackByIdMd: '/api/v1/share/feedback/:id.md',
  v1ShareFeedbackByIdAttachmentByIdx: '/api/v1/share/feedback/:id/attachment/:idx',
  v1ShareIssuesByIdLogs: '/api/v1/share/issues/:id/logs.txt',
  // Parsed `bp.memMap` of the shared sample with sampled-heap frames
  // symbolicated — the Memory tab's data for text-first (AI) readers.
  v1ShareIssuesByIdMemory: '/api/v1/share/issues/:id/memory.json',
  // The same memory map as a markdown digest, optionally narrowed to
  // `?section=` keys (memoryDigest.MEMORY_SECTIONS) — the share brief inlines
  // only a summary and links here for detail.
  v1ShareIssuesByIdMemoryMd: '/api/v1/share/issues/:id/memory.md',
  // Every captured sample of the issue plus build / OS / device breakdowns.
  v1ShareIssuesByIdEventsMd: '/api/v1/share/issues/:id/events.md',
  v1ShareIssuesByIdScreenshotByFrameIndex: '/api/v1/share/issues/:id/screenshot/:frameIndex',
  // Event-level images beyond the storyboard rail — crash/report screenshot,
  // context screenshot, ANR screenshots, video thumbnail. `key` is one of
  // `screenshot` | `context` | `video-thumb` | `anr-<n>`.
  v1ShareIssuesByIdImageByKey: '/api/v1/share/issues/:id/image/:key',
  // Ungated OG-image for the pasted dashboard deep-link (/issues?selected=<id>).
  // UNLIKE the routes above it does NOT check the 24h window — a valid issue id
  // is enough (Slack's image proxy fetches og:image with no share window). Only
  // ever referenced from the /issues social-crawler unfurl; resolves the best
  // single image (storyboard frame 0, else newest crash screenshot).
  v1ShareIssuesByIdUnfurlImage: '/api/v1/share/issues/:id/unfurl-image',
  // Sample video (pinned share sample, else newest with video).
  v1ShareIssuesByIdVideo: '/api/v1/share/issues/:id/video',
  // Auth-required (route-level requireAuth): lets a logged-in dashboard user
  // resolve the share URL to its project so the SPA can redirect to the full
  // /issues view instead of the sanitized public page. Bypasses the 24h
  // window — auth users only need project membership.
  v1ShareIssuesByIdUpgrade: '/api/v1/share/issues/:id/upgrade',
  // Write actions on a shared issue — no auth, gated on the open 24h share
  // window. Lets an AI following the shared link post a fix message and resolve
  // the issue it was handed. Attributed to a caller-provided author (fallback
  // "AI (shared link)") so team members can tell a shared-link agent acted.
  v1ShareIssuesByIdComment: '/api/v1/share/issues/:id/comment',
  v1ShareIssuesByIdResolve: '/api/v1/share/issues/:id/resolve',
  v1DevicesByRefDetails: '/api/v1/devices/:ref/details',
  v1DevicesByRefLogSessions: '/api/v1/devices/:ref/log-sessions',
  v1DevicesByRefNickname: '/api/v1/devices/:ref/nickname',
  // Controlled sessions (features/controlSessions) — PAT/JWT, see toolCatalog.ts.
  v1ControlSessions: '/api/v1/control-sessions',
  v1ControlSessionsTools: '/api/v1/control-sessions/tools',
  v1ControlSessionsScenariosReport: '/api/v1/control-sessions/scenarios/report',
  v1ControlSessionsScenariosAttempts: '/api/v1/control-sessions/scenarios/attempts',
  v1ControlSessionsGoalCatalog: '/api/v1/control-sessions/goal-catalog',
  v1ControlSessionsUnitTestRuns: '/api/v1/control-sessions/unit-tests/runs',
  v1ControlSessionsUnitTestRunsByRunId: '/api/v1/control-sessions/unit-tests/runs/:run_id',
  v1ControlSessionsTestsLibrary: '/api/v1/control-sessions/tests/library',
  v1ControlSessionsTestsStart: '/api/v1/control-sessions/tests/start',
  v1ControlSessionsTestsLibraryByTestId: '/api/v1/control-sessions/tests/library/:test_id',
  v1ControlSessionsErrorsFilters: '/api/v1/control-sessions/errors/filters',
  v1ControlSessionsKnowledge: '/api/v1/control-sessions/knowledge',
  v1ControlSessionsKnowledgeByKey: '/api/v1/control-sessions/knowledge/:key',
  v1ControlSessionsTestPrompt: '/api/v1/control-sessions/test-prompt',
  v1ControlSessionsAccounts: '/api/v1/control-sessions/accounts',
  v1ControlSessionsAgentProviders: '/api/v1/control-sessions/agent-providers',
  v1ControlSessionsAutomationTriggers: '/api/v1/control-sessions/automation/triggers',
  v1ControlSessionsAutomationTriggersByTriggerId:
    '/api/v1/control-sessions/automation/triggers/:trigger_id',
  v1ControlSessionsAutomationTriggersByTriggerIdRun:
    '/api/v1/control-sessions/automation/triggers/:trigger_id/run',
  v1ControlSessionsAutomationFullRun: '/api/v1/control-sessions/automation/full-run',
  v1ControlSessionsAutomationFixRun: '/api/v1/control-sessions/automation/fix-run',
  v1ControlSessionsAutomationRuns: '/api/v1/control-sessions/automation/runs',
  v1ControlSessionsAutomationRunsByRunIdCancel:
    '/api/v1/control-sessions/automation/runs/:run_id/cancel',
  v1ControlSessionsAutomationLive: '/api/v1/control-sessions/automation/live',
  v1ControlSessionsAutomationGoalRoutes: '/api/v1/control-sessions/automation/goal-routes',
  v1ControlSessionsAutomationScriptVersionsByScriptId:
    '/api/v1/control-sessions/automation/script-versions/:script_id',
  v1ControlSessionsErrorsFiltersByFilterId: '/api/v1/control-sessions/errors/filters/:filter_id',
  v1ControlSessionsBySessionId: '/api/v1/control-sessions/:session_id',
  v1ControlSessionsBySessionIdClose: '/api/v1/control-sessions/:session_id/close',
  v1ControlSessionsBySessionIdPause: '/api/v1/control-sessions/:session_id/pause',
  v1ControlSessionsBySessionIdResume: '/api/v1/control-sessions/:session_id/resume',
  v1ControlSessionsBySessionIdBudget: '/api/v1/control-sessions/:session_id/budget',
  v1ControlSessionsBySessionIdNotes: '/api/v1/control-sessions/:session_id/notes',
  v1ControlSessionsBySessionIdKnowledge: '/api/v1/control-sessions/:session_id/knowledge',
  v1ControlSessionsBySessionIdMessages: '/api/v1/control-sessions/:session_id/messages',
  v1ControlSessionsBySessionIdEvents: '/api/v1/control-sessions/:session_id/events',
  v1ControlSessionsBySessionIdResults: '/api/v1/control-sessions/:session_id/results',
  v1ControlSessionsBySessionIdLogs: '/api/v1/control-sessions/:session_id/logs',
  v1ControlSessionsBySessionIdLogSources: '/api/v1/control-sessions/:session_id/log-sources',
  v1ControlSessionsBySessionIdLogSearch: '/api/v1/control-sessions/:session_id/log-search',
  v1ControlSessionsBySessionIdLogDownload: '/api/v1/control-sessions/:session_id/log-download',
  v1ControlSessionsBySessionIdLogsBySourceIdDownload:
    '/api/v1/control-sessions/:session_id/logs/:source_id/download',
  v1ControlSessionsBySessionIdAnalysis: '/api/v1/control-sessions/:session_id/analysis',
  v1ControlSessionsBySessionIdUiTimeline: '/api/v1/control-sessions/:session_id/ui-timeline',
  v1ControlSessionsBySessionIdUiMap: '/api/v1/control-sessions/:session_id/ui-map',
  v1ControlSessionsBySessionIdVideo: '/api/v1/control-sessions/:session_id/video',
  v1ControlSessionsBySessionIdVideoSegmentsBySegmentId:
    '/api/v1/control-sessions/:session_id/video/segments/:segment_id',
  v1ControlSessionsBySessionIdVideoBySeatDownload:
    '/api/v1/control-sessions/:session_id/video/:seat/download',
  v1ControlSessionsBySessionIdPromotePreview:
    '/api/v1/control-sessions/:session_id/promote_preview',
  v1ControlSessionsBySessionIdRerun: '/api/v1/control-sessions/:session_id/rerun',
  v1ControlSessionsBySessionIdSeats: '/api/v1/control-sessions/:session_id/seats',
  v1ControlSessionsBySessionIdSeatsScale: '/api/v1/control-sessions/:session_id/seats/scale',
  v1ControlSessionsBySessionIdSeatsBySeat: '/api/v1/control-sessions/:session_id/seats/:seat',
  v1ControlSessionsBySessionIdSeatsBySeatRelease:
    '/api/v1/control-sessions/:session_id/seats/:seat/release',
  v1ControlSessionsBySessionIdSeatsBySeatOpen:
    '/api/v1/control-sessions/:session_id/seats/:seat/open',
  v1ControlSessionsBySessionIdSeatsBySeatClose:
    '/api/v1/control-sessions/:session_id/seats/:seat/close',
  v1ControlSessionsBySessionIdSeatsBySeatRecover:
    '/api/v1/control-sessions/:session_id/seats/:seat/recover',
  v1ControlSessionsBySessionIdSeatsBySeatColdStart:
    '/api/v1/control-sessions/:session_id/seats/:seat/cold-start',
  v1ControlSessionsBySessionIdSeatsBySeatLoad:
    '/api/v1/control-sessions/:session_id/seats/:seat/load',
  v1ControlSessionsBySessionIdSeatsBySeatHealth:
    '/api/v1/control-sessions/:session_id/seats/:seat/health',
  v1ControlSessionsBySessionIdSeatsBySeatTakeover:
    '/api/v1/control-sessions/:session_id/seats/:seat/takeover',
  v1ControlSessionsBySessionIdSeatsBySeatHandback:
    '/api/v1/control-sessions/:session_id/seats/:seat/handback',
  v1ControlSessionsBySessionIdSeatsBySeatStreamOffer:
    '/api/v1/control-sessions/:session_id/seats/:seat/stream/offer',
  v1ControlSessionsBySessionIdSeatsBySeatStreamIce:
    '/api/v1/control-sessions/:session_id/seats/:seat/stream/ice',
  v1ControlSessionsBySessionIdSeatsBySeatStreamClose:
    '/api/v1/control-sessions/:session_id/seats/:seat/stream/close',
  v1ControlSessionsBySessionIdSeatsBySeatStreamInput:
    '/api/v1/control-sessions/:session_id/seats/:seat/stream/input',
  v1ControlSessionsBySessionIdSeatsBySeatScreenshot:
    '/api/v1/control-sessions/:session_id/seats/:seat/screenshot',
  v1ControlSessionsBySessionIdSeatsBySeatPeek:
    '/api/v1/control-sessions/:session_id/seats/:seat/peek',
  v1ControlSessionsBySessionIdSeatsBySeatUiTree:
    '/api/v1/control-sessions/:session_id/seats/:seat/ui_tree',
  v1ControlSessionsBySessionIdSeatsBySeatAccounts:
    '/api/v1/control-sessions/:session_id/seats/:seat/accounts',
  v1ControlSessionsBySessionIdSeatsBySeatState:
    '/api/v1/control-sessions/:session_id/seats/:seat/state',
  v1ControlSessionsBySessionIdSeatsBySeatLogs:
    '/api/v1/control-sessions/:session_id/seats/:seat/logs',
  v1ControlSessionsBySessionIdSeatsBySeatAct:
    '/api/v1/control-sessions/:session_id/seats/:seat/act',
  v1ControlSessionsBySessionIdSeatsBySeatInput:
    '/api/v1/control-sessions/:session_id/seats/:seat/input',
  v1ControlSessionsBySessionIdSeatsBySeatPcInput:
    '/api/v1/control-sessions/:session_id/seats/:seat/pc_input',
  v1ControlSessionsBySessionIdSeatsBySeatDeviceInput:
    '/api/v1/control-sessions/:session_id/seats/:seat/device_input',
  v1ControlSessionsBySessionIdSeatsBySeatTools:
    '/api/v1/control-sessions/:session_id/seats/:seat/tools',
  v1ControlSessionsBySessionIdSeatsBySeatToolsCall:
    '/api/v1/control-sessions/:session_id/seats/:seat/tools/call',
  v1ControlSessionsBySessionIdSeatsBySeatScript:
    '/api/v1/control-sessions/:session_id/seats/:seat/script',
  v1ControlSessionsBySessionIdSeatsBySeatEditorExec:
    '/api/v1/control-sessions/:session_id/seats/:seat/editor_exec',
  v1ControlSessionsBySessionIdBuild: '/api/v1/control-sessions/:session_id/build',
  v1ControlSessionsBySessionIdBuildPin: '/api/v1/control-sessions/:session_id/build/pin',
  v1ControlSessionsBySessionIdBuildOverlay: '/api/v1/control-sessions/:session_id/build/overlay',
  v1ControlSessionsBySessionIdBuildPrepare: '/api/v1/control-sessions/:session_id/build/prepare',
  v1ControlSessionsBySessionIdBuildDeploy: '/api/v1/control-sessions/:session_id/build/deploy',
  v1ControlSessionsBySessionIdBuildRelease: '/api/v1/control-sessions/:session_id/build/release',
  v1ControlSessionsBySessionIdOpsByOpId: '/api/v1/control-sessions/:session_id/ops/:op_id',
  v1ControlSessionsBySessionIdOpsByOpIdCancel:
    '/api/v1/control-sessions/:session_id/ops/:op_id/cancel',
  v1ControlSessionsBySessionIdScenarios: '/api/v1/control-sessions/:session_id/scenarios',
  v1ControlSessionsBySessionIdScenariosRun: '/api/v1/control-sessions/:session_id/scenarios/run',
  v1ControlSessionsBySessionIdScenariosReport:
    '/api/v1/control-sessions/:session_id/scenarios/report',
  v1ControlSessionsBySessionIdScenariosBisect:
    '/api/v1/control-sessions/:session_id/scenarios/bisect',
  v1ControlSessionsBySessionIdState: '/api/v1/control-sessions/:session_id/state',
  v1ControlSessionsBySessionIdStateByKey: '/api/v1/control-sessions/:session_id/state/:key',
  v1ControlSessionsBySessionIdSignalsByName: '/api/v1/control-sessions/:session_id/signals/:name',
  v1ControlSessionsBySessionIdBarriersByName: '/api/v1/control-sessions/:session_id/barriers/:name',
  v1ControlSessionsBySessionIdScripts: '/api/v1/control-sessions/:session_id/scripts',
  v1ControlSessionsBySessionIdAutomationNext:
    '/api/v1/control-sessions/:session_id/automation/next',
  v1ControlSessionsBySessionIdAutomationDone:
    '/api/v1/control-sessions/:session_id/automation/done',
  v1ControlSessionsBySessionIdGoalScripts: '/api/v1/control-sessions/:session_id/goal-scripts',
  v1ControlSessionsBySessionIdScriptsByScriptIdVersions:
    '/api/v1/control-sessions/:session_id/scripts/:script_id/versions',
  v1ControlSessionsBySessionIdSeatsBySeatTestRun:
    '/api/v1/control-sessions/:session_id/seats/:seat/test-run',
  v1ControlSessionsBySessionIdSeatsBySeatTestRunResolve:
    '/api/v1/control-sessions/:session_id/seats/:seat/test-run/resolve',
  v1ControlSessionsBySessionIdSeatsBySeatTestRunRecorded:
    '/api/v1/control-sessions/:session_id/seats/:seat/test-run/recorded',
  v1ControlSessionsBySessionIdScriptsByScriptId:
    '/api/v1/control-sessions/:session_id/scripts/:script_id',
  v1ControlSessionsBySessionIdScriptsByScriptIdPromote:
    '/api/v1/control-sessions/:session_id/scripts/:script_id/promote',
  v1ControlSessionsBySessionIdScriptsByScriptIdApprove:
    '/api/v1/control-sessions/:session_id/scripts/:script_id/approve',
  v1ControlSessionsBySessionIdIssues: '/api/v1/control-sessions/:session_id/issues',
  v1ControlSessionsBySessionIdGoals: '/api/v1/control-sessions/:session_id/goals',
  v1ControlSessionsBySessionIdAgent: '/api/v1/control-sessions/:session_id/agent',
  v1ControlSessionsBySessionIdMcp: '/api/v1/control-sessions/:session_id/mcp',
  v1ControlSessionsBySessionIdBriefMd: '/api/v1/control-sessions/:session_id/brief.md',
  v1ControlSessionsBySessionIdTests: '/api/v1/control-sessions/:session_id/tests',
  v1ControlSessionsBySessionIdTestsRun: '/api/v1/control-sessions/:session_id/tests/run',
  v1ControlSessionsBySessionIdTestsReport: '/api/v1/control-sessions/:session_id/tests/report',
  v1ControlSessionsBySessionIdFrames: '/api/v1/control-sessions/:session_id/frames',
  v1ControlSessionsBySessionIdFramesByFrameRef:
    '/api/v1/control-sessions/:session_id/frames/:frame_ref',
  v1Runners: '/api/v1/runners',
  v1PlasticFind: '/api/v1/plastic/find',
  v1IssuesFind: '/api/v1/issues/find',
  v1LogSessions: '/api/v1/log-sessions',
  v1LogSessionsBySessionIdIssues: '/api/v1/log-sessions/:sessionId/issues',
  v1LogSessionsBySessionIdLog: '/api/v1/log-sessions/:sessionId/log',
  v1LogSessionsBySessionIdShare: '/api/v1/log-sessions/:sessionId/share',
  v1LogSessionsBySessionIdShareByToken: '/api/v1/log-sessions/:sessionId/share/:token',
  // Mint an AI-shareable brief for an AD-HOC group of log sessions (the manual
  // Grouped Logs view). Server merges them into a synthetic joint log + mints a
  // normal log share on it, so the public /share/logs/:token.md serves it.
  v1GroupedLogsShare: '/api/v1/grouped-logs/share',
  // Public log share — read-only views of a log session (or a specific line
  // range) keyed on an unguessable token. Allowlisted via PUBLIC_PATH_PREFIXES
  // (/api/v1/share/ already covers it). Token-validated per request. The JSON
  // form takes filter/window params (grep/level/tag/pid/tail/before/after) and
  // hands back paging cursors; the `.md` form is the self-describing agent view
  // that documents those params inline. Register `.md` BEFORE the bare token.
  v1ShareAgentByToken: '/api/v1/share/agent/:token',
  v1ShareAgentByTokenCli: '/api/v1/share/agent/:token/bp-issues.mjs',
  v1ShareAgentByTokenWrapper: '/api/v1/share/agent/:token/bp',
  v1ShareAgentByTokenKey: '/api/v1/share/agent/:token/key',
  v1AgentShareBrief: '/api/v1/agent-share/brief',
  projectsByProjectIdAgentShares: '/api/projects/:projectId/agent-shares',
  v1ShareLogsByTokenMd: '/api/v1/share/logs/:token.md',
  v1ShareLogsByToken: '/api/v1/share/logs/:token',
  v1ShareSessionsByTokenMd: '/api/v1/share/sessions/:token.md',
  v1ShareSessionsByToken: '/api/v1/share/sessions/:token',
  v1ShareSessionsByTokenLogs: '/api/v1/share/sessions/:token/logs',
  v1ShareSessionsByTokenParticipants: '/api/v1/share/sessions/:token/participants',
  v1ShareSessionsByTokenDeviceScreenshot:
    '/api/v1/share/sessions/:token/participants/:deviceId/screenshot',
  v1ShareSessionsByTokenDeviceInput: '/api/v1/share/sessions/:token/participants/:deviceId/input',
  v1ShareSessionsByTokenDeviceScript: '/api/v1/share/sessions/:token/participants/:deviceId/script',
  v1Mappings: '/api/v1/mappings',
  v1MappingsPresign: '/api/v1/mappings/presign',
  v1MappingsById: '/api/v1/mappings/:id',
  versions: '/api/versions',

  // ── Plastic Cloud integration (org-scoped) ─────────────────────────────
  plasticIntegrations: '/api/plastic/integrations',
  plasticIntegrationsById: '/api/plastic/integrations/:id',
  plasticIntegrationsByIdBackfillPaste: '/api/plastic/integrations/:id/backfill-paste',
  plasticIntegrationsByIdPull: '/api/plastic/integrations/:id/pull',
  // POST: save the NON-SECRET cm service-account identity (username + working
  // mode) used to build the profile in the isolated plastic-cm sidecar. The
  // secret token lives only in the sidecar env, never in the app DB.
  plasticIntegrationsByIdCmProfile: '/api/plastic/integrations/:id/cm-profile',
  // GET: "is the plastic-cm sidecar up + authenticated?" — pings cm api.
  plasticIntegrationsByIdTestApi: '/api/plastic/integrations/:id/test-api',
  // GET: scoped probe — can the sidecar credential see this integration's
  // bound repos (lists branches per bound repo)?
  plasticIntegrationsByIdTestConnection: '/api/plastic/integrations/:id/test-connection',
  // GET ?repo= : branches in a repo (powers the Create Review picker).
  plasticIntegrationsByIdBranches: '/api/plastic/integrations/:id/branches',
  plasticIntegrationsByIdRepos: '/api/plastic/integrations/:id/repos',
  // GET ?repo=&branch=&limit= : recent changesets (powers the picker).
  plasticIntegrationsByIdChangesets: '/api/plastic/integrations/:id/changesets',
  // GET: recent cm invocations the plastic-cm sidecar ran (tokens redacted) —
  // powers the dashboard "Sidecar log" diagnostic popup.
  plasticIntegrationsByIdSidecarLog: '/api/plastic/integrations/:id/sidecar-log',

  // ── Git (GitHub App) integration (org-scoped) ──────────────────────────
  gitIntegrations: '/api/git/integrations',
  gitIntegrationsById: '/api/git/integrations/:id',
  gitIntegrationsByIdPull: '/api/git/integrations/:id/pull',
  gitInstallStart: '/api/git/install/start',
  gitInstallCallback: '/api/git/install/callback',
  // App-level (server-deployment-wide) GitHub App credentials live in the
  // settings table. Admin-only.
  gitAppConfig: '/api/git/app-config',

  // ── Provider-agnostic source data ──────────────────────────────────────
  sourceChangesets: '/api/source/changesets',
  sourceChangesetsBetween: '/api/source/changesets/between',
  sourceChangesetsRecent: '/api/source/changesets/recent',
  sourceChangesetsBranches: '/api/source/changesets/branches',
  // Ad-hoc diff preview for a set of selected changesets (Resolve dialog's
  // "Review diff" popup). `/diff` = changed-file list; `/diff/file` = the two
  // revisions for one path. Read-only; no review entity is created.
  sourceChangesetsDiff: '/api/source/changesets/diff',
  sourceChangesetsDiffFile: '/api/source/changesets/diff/file',
  sourceChangesetsBranchFiles: '/api/source/changesets/branch-files',
  // Per-project binding to a (Plastic | Git, repository) pair. Tells the
  // ingest paths which project should receive incoming changesets, and
  // tells the dashboard which integration to query for autocomplete.
  sourceBinding: '/api/source/binding',
  sourceSync: '/api/source/sync',

  // Plastic-NATIVE code reviews (reviews + comments live on the cloud server,
  // proxied via plasticReviewService → cloud REST API). Project-scoped via
  // ?projectId=. Supersedes the DB-backed plasticCodeReviews* below.
  plasticReviews: '/api/plastic/reviews',
  plasticReviewsById: '/api/plastic/reviews/:id',
  plasticReviewsByIdComments: '/api/plastic/reviews/:id/comments',
  plasticReviewsByIdFiles: '/api/plastic/reviews/:id/files',
  plasticReviewsByIdDiff: '/api/plastic/reviews/:id/diff',

  // ── Code reviews — Plastic-backed, with inline diff + comments stored in
  // Bugpunch. See codeReviewService.ts + plasticCli.ts. URL stays under
  // /api/plastic/ since the underlying engine is `cm codereview`.
  plasticCodeReviews: '/api/plastic/code-reviews',
  plasticCodeReviewsById: '/api/plastic/code-reviews/:id',
  plasticCodeReviewsByIdFiles: '/api/plastic/code-reviews/:id/files',
  plasticCodeReviewsByIdDiff: '/api/plastic/code-reviews/:id/diff',
  plasticCodeReviewsByIdComments: '/api/plastic/code-reviews/:id/comments',
  plasticCodeReviewsByIdCommentsByCommentId: '/api/plastic/code-reviews/:id/comments/:commentId',
  plasticCodeReviewsByIdIssues: '/api/plastic/code-reviews/:id/issues',
  plasticCodeReviewsByIdIssuesByIssueId: '/api/plastic/code-reviews/:id/issues/:issueId',
  // Issues → reviews lookup, mirroring the existing per-issue changeset
  // hydration path used by the IssueDetailPanel.
  issuesByIdCodeReviews: '/api/issues/:id/code-reviews',

  // Extra IDE proxy paths the auto-walker missed.
  devicesByDeviceIdProxySceneCameraRaycast: '/api/devices/:deviceId/proxy/scene-camera/raycast',
  devicesByDeviceIdProxySettingsByGroup: '/api/devices/:deviceId/proxy/settings/:group',
  // Proxy base URL — used by Monaco IntelliSense providers as a prefix to
  // append arbitrary sub-paths, and by ScriptPanel/DebugToolsPage when they
  // pass a "device proxy base" down to a child editor component.
  devicesByDeviceIdProxyBase: '/api/devices/:deviceId/proxy',

  devicesByDeviceIdDatabasesClose: '/api/devices/:deviceId/databases/close',
  devicesByDeviceIdDatabasesDelete: '/api/devices/:deviceId/databases/delete',
  devicesByDeviceIdDatabasesInsert: '/api/devices/:deviceId/databases/insert',
  devicesByDeviceIdDatabasesOpen: '/api/devices/:deviceId/databases/open',
  devicesByDeviceIdDatabasesProviders: '/api/devices/:deviceId/databases/providers',
  devicesByDeviceIdDatabasesQuery: '/api/devices/:deviceId/databases/query',
  devicesByDeviceIdDatabasesRaw: '/api/devices/:deviceId/databases/raw',
  devicesByDeviceIdDatabasesScan: '/api/devices/:deviceId/databases/scan',
  devicesByDeviceIdDatabasesSchema: '/api/devices/:deviceId/databases/schema',
  devicesByDeviceIdDatabasesUpdate: '/api/devices/:deviceId/databases/update',
  devicesByDeviceIdProxyApply: '/api/devices/:deviceId/proxy/apply',
  devicesByDeviceIdProxyAssetsList: '/api/devices/:deviceId/proxy/assets/list',
  devicesByDeviceIdProxyAssetsLoad: '/api/devices/:deviceId/proxy/assets/load',
  devicesByDeviceIdProxyAssetsTypes: '/api/devices/:deviceId/proxy/assets/types',
  devicesByDeviceIdProxyCameras: '/api/devices/:deviceId/proxy/cameras',
  devicesByDeviceIdProxyCapture: '/api/devices/:deviceId/proxy/capture',
  devicesByDeviceIdProxyChildren: '/api/devices/:deviceId/proxy/children',
  devicesByDeviceIdProxyDeviceInfo: '/api/devices/:deviceId/proxy/device-info',
  devicesByDeviceIdProxyDiagnose: '/api/devices/:deviceId/proxy/diagnose',
  devicesByDeviceIdProxyFields: '/api/devices/:deviceId/proxy/fields',
  devicesByDeviceIdProxyFilesDelete: '/api/devices/:deviceId/proxy/files/delete',
  devicesByDeviceIdProxyFilesInfo: '/api/devices/:deviceId/proxy/files/info',
  devicesByDeviceIdProxyFilesList: '/api/devices/:deviceId/proxy/files/list',
  devicesByDeviceIdProxyFilesMkdir: '/api/devices/:deviceId/proxy/files/mkdir',
  devicesByDeviceIdProxyFilesPaths: '/api/devices/:deviceId/proxy/files/paths',
  devicesByDeviceIdProxyFilesRead: '/api/devices/:deviceId/proxy/files/read',
  devicesByDeviceIdProxyFilesWrite: '/api/devices/:deviceId/proxy/files/write',
  devicesByDeviceIdProxyFilesZipProgress: '/api/devices/:deviceId/proxy/files/zip/progress',
  devicesByDeviceIdProxyFilesZipResult: '/api/devices/:deviceId/proxy/files/zip/result',
  devicesByDeviceIdProxyFilesZipStart: '/api/devices/:deviceId/proxy/files/zip/start',
  devicesByDeviceIdProxyGameConfig: '/api/devices/:deviceId/proxy/game-config',
  devicesByDeviceIdProxyGameobject: '/api/devices/:deviceId/proxy/gameobject',
  devicesByDeviceIdProxyGameobjectAddComponent:
    '/api/devices/:deviceId/proxy/gameobject/add-component',
  devicesByDeviceIdProxyGameobjectComponentTypes:
    '/api/devices/:deviceId/proxy/gameobject/component-types',
  devicesByDeviceIdProxyGameobjectRemoveComponent:
    '/api/devices/:deviceId/proxy/gameobject/remove-component',
  devicesByDeviceIdProxyHierarchyDelete: '/api/devices/:deviceId/proxy/hierarchy/delete',
  devicesByDeviceIdProxyInputPointer: '/api/devices/:deviceId/proxy/input/pointer',
  devicesByDeviceIdProxyInputSwipe: '/api/devices/:deviceId/proxy/input/swipe',
  devicesByDeviceIdProxyInputTap: '/api/devices/:deviceId/proxy/input/tap',
  devicesByDeviceIdProxyInputTouches: '/api/devices/:deviceId/proxy/input/touches',
  devicesByDeviceIdProxyInspect: '/api/devices/:deviceId/proxy/inspect',
  devicesByDeviceIdProxyInspectorDerivedTypes:
    '/api/devices/:deviceId/proxy/inspector/derived-types',
  devicesByDeviceIdProxyInspectorEnum: '/api/devices/:deviceId/proxy/inspector/enum',
  devicesByDeviceIdProxyInspectorLayers: '/api/devices/:deviceId/proxy/inspector/layers',
  devicesByDeviceIdProxyInspectorList: '/api/devices/:deviceId/proxy/inspector/list',
  devicesByDeviceIdProxyInspectorListAdd: '/api/devices/:deviceId/proxy/inspector/list/add',
  devicesByDeviceIdProxyInspectorListMove: '/api/devices/:deviceId/proxy/inspector/list/move',
  devicesByDeviceIdProxyInspectorListRemove: '/api/devices/:deviceId/proxy/inspector/list/remove',
  devicesByDeviceIdProxyInspectorListSet: '/api/devices/:deviceId/proxy/inspector/list/set',
  devicesByDeviceIdProxyInspectorObjects: '/api/devices/:deviceId/proxy/inspector/objects',
  devicesByDeviceIdProxyInspectorSortingLayers:
    '/api/devices/:deviceId/proxy/inspector/sorting-layers',
  devicesByDeviceIdProxyInspectorTags: '/api/devices/:deviceId/proxy/inspector/tags',
  devicesByDeviceIdProxyInspectorUploadAsset: '/api/devices/:deviceId/proxy/inspector/upload-asset',
  devicesByDeviceIdProxyInvoke: '/api/devices/:deviceId/proxy/invoke',
  devicesByDeviceIdProxyLog: '/api/devices/:deviceId/proxy/log',
  devicesByDeviceIdProxyMaterialsCopyFrom: '/api/devices/:deviceId/proxy/materials/copy-from',
  devicesByDeviceIdProxyMaterialsList: '/api/devices/:deviceId/proxy/materials/list',
  devicesByDeviceIdProxyMaterialsProperties: '/api/devices/:deviceId/proxy/materials/properties',
  devicesByDeviceIdProxyMaterialsRenderQueue: '/api/devices/:deviceId/proxy/materials/render-queue',
  devicesByDeviceIdProxyMaterialsResetDefaults:
    '/api/devices/:deviceId/proxy/materials/reset-defaults',
  devicesByDeviceIdProxyMaterialsSetKeyword: '/api/devices/:deviceId/proxy/materials/set-keyword',
  devicesByDeviceIdProxyMaterialsSetProperty: '/api/devices/:deviceId/proxy/materials/set-property',
  devicesByDeviceIdProxyMaterialsSetShader: '/api/devices/:deviceId/proxy/materials/set-shader',
  devicesByDeviceIdProxyMaterialsShaders: '/api/devices/:deviceId/proxy/materials/shaders',
  devicesByDeviceIdProxyMaterialsTexture: '/api/devices/:deviceId/proxy/materials/texture',
  devicesByDeviceIdProxyMaterialsTextures: '/api/devices/:deviceId/proxy/materials/textures',
  devicesByDeviceIdProxyMaterialsThumbnail: '/api/devices/:deviceId/proxy/materials/thumbnail',
  devicesByDeviceIdProxyMaterialsUploadTexture:
    '/api/devices/:deviceId/proxy/materials/upload-texture',
  devicesByDeviceIdProxyMemoryAssets: '/api/devices/:deviceId/proxy/memory/assets',
  devicesByDeviceIdProxyMemoryDelete: '/api/devices/:deviceId/proxy/memory/delete',
  devicesByDeviceIdProxyMemoryDownload: '/api/devices/:deviceId/proxy/memory/download',
  devicesByDeviceIdProxyMemoryList: '/api/devices/:deviceId/proxy/memory/list',
  devicesByDeviceIdProxyMemoryPreview: '/api/devices/:deviceId/proxy/memory/preview',
  devicesByDeviceIdProxyMemorySnapshot: '/api/devices/:deviceId/proxy/memory/snapshot',
  devicesByDeviceIdProxyMemoryStats: '/api/devices/:deviceId/proxy/memory/stats',
  devicesByDeviceIdProxyMemoryStatus: '/api/devices/:deviceId/proxy/memory/status',
  devicesByDeviceIdProxyMemoryUsers: '/api/devices/:deviceId/proxy/memory/users',
  devicesByDeviceIdProxyMethods: '/api/devices/:deviceId/proxy/methods',
  devicesByDeviceIdProxyPlayerprefsDelete: '/api/devices/:deviceId/proxy/playerprefs/delete',
  devicesByDeviceIdProxyPlayerprefsDeleteAll: '/api/devices/:deviceId/proxy/playerprefs/delete-all',
  devicesByDeviceIdProxyPlayerprefsList: '/api/devices/:deviceId/proxy/playerprefs/list',
  devicesByDeviceIdProxyPlayerprefsSet: '/api/devices/:deviceId/proxy/playerprefs/set',
  devicesByDeviceIdProxyRun: '/api/devices/:deviceId/proxy/run',
  devicesByDeviceIdProxySceneCameraAspect: '/api/devices/:deviceId/proxy/scene-camera/aspect',
  devicesByDeviceIdProxySceneCameraBounds: '/api/devices/:deviceId/proxy/scene-camera/bounds',
  devicesByDeviceIdProxySceneCameraColliderTransforms:
    '/api/devices/:deviceId/proxy/scene-camera/collider-transforms',
  devicesByDeviceIdProxySceneCameraColliders: '/api/devices/:deviceId/proxy/scene-camera/colliders',
  devicesByDeviceIdProxySceneCameraDrawDistance:
    '/api/devices/:deviceId/proxy/scene-camera/draw-distance',
  devicesByDeviceIdProxySceneCameraFly: '/api/devices/:deviceId/proxy/scene-camera/fly',
  devicesByDeviceIdProxySceneCameraFocus: '/api/devices/:deviceId/proxy/scene-camera/focus',
  devicesByDeviceIdProxySceneCameraGrid: '/api/devices/:deviceId/proxy/scene-camera/grid',
  devicesByDeviceIdProxySceneCameraLook: '/api/devices/:deviceId/proxy/scene-camera/look',
  devicesByDeviceIdProxySceneCameraOrbit: '/api/devices/:deviceId/proxy/scene-camera/orbit',
  devicesByDeviceIdProxySceneCameraPan: '/api/devices/:deviceId/proxy/scene-camera/pan',
  devicesByDeviceIdProxySceneCameraProjection:
    '/api/devices/:deviceId/proxy/scene-camera/projection',
  devicesByDeviceIdProxySceneCameraRenderMode:
    '/api/devices/:deviceId/proxy/scene-camera/render-mode',
  devicesByDeviceIdProxySceneCameraSnap: '/api/devices/:deviceId/proxy/scene-camera/snap',
  devicesByDeviceIdProxySceneCameraStart: '/api/devices/:deviceId/proxy/scene-camera/start',
  devicesByDeviceIdProxySceneCameraState: '/api/devices/:deviceId/proxy/scene-camera/state',
  devicesByDeviceIdProxySceneCameraStop: '/api/devices/:deviceId/proxy/scene-camera/stop',
  devicesByDeviceIdProxySceneCameraTransform: '/api/devices/:deviceId/proxy/scene-camera/transform',
  devicesByDeviceIdProxySceneCameraUi: '/api/devices/:deviceId/proxy/scene-camera/ui',
  devicesByDeviceIdProxySceneCameraZoom: '/api/devices/:deviceId/proxy/scene-camera/zoom',
  devicesByDeviceIdProxyScenes: '/api/devices/:deviceId/proxy/scenes',
  devicesByDeviceIdProxySettings: '/api/devices/:deviceId/proxy/settings',
  devicesByDeviceIdProxySettingsLayersCollision:
    '/api/devices/:deviceId/proxy/settings/layers/collision',
  devicesByDeviceIdProxyShaderProfileCancel: '/api/devices/:deviceId/proxy/shader-profile/cancel',
  devicesByDeviceIdProxyShaderProfileGroups: '/api/devices/:deviceId/proxy/shader-profile/groups',
  devicesByDeviceIdProxyShaderProfileResult: '/api/devices/:deviceId/proxy/shader-profile/result',
  devicesByDeviceIdProxyShaderProfileSpotlight:
    '/api/devices/:deviceId/proxy/shader-profile/spotlight',
  devicesByDeviceIdProxyShaderProfileStart: '/api/devices/:deviceId/proxy/shader-profile/start',
  devicesByDeviceIdProxyShaderProfileStatus: '/api/devices/:deviceId/proxy/shader-profile/status',
  devicesByDeviceIdProxyTexturesFull: '/api/devices/:deviceId/proxy/textures/full',
  devicesByDeviceIdProxyTexturesInfo: '/api/devices/:deviceId/proxy/textures/info',
  devicesByDeviceIdProxyTexturesList: '/api/devices/:deviceId/proxy/textures/list',
  devicesByDeviceIdProxyTexturesReferences: '/api/devices/:deviceId/proxy/textures/references',
  devicesByDeviceIdProxyTexturesThumbnail: '/api/devices/:deviceId/proxy/textures/thumbnail',
  devicesByDeviceIdProxyWatchAdd: '/api/devices/:deviceId/proxy/watch/add',
  devicesByDeviceIdProxyWatchApply: '/api/devices/:deviceId/proxy/watch/apply',
  devicesByDeviceIdProxyWatchApplyBatch: '/api/devices/:deviceId/proxy/watch/apply-batch',
  devicesByDeviceIdProxyWatchClear: '/api/devices/:deviceId/proxy/watch/clear',
  devicesByDeviceIdProxyWatchList: '/api/devices/:deviceId/proxy/watch/list',
  devicesByDeviceIdProxyWatchPoll: '/api/devices/:deviceId/proxy/watch/poll',
  devicesByDeviceIdProxyWatchRemove: '/api/devices/:deviceId/proxy/watch/remove',
  devicesByDeviceIdProxyWatchRescan: '/api/devices/:deviceId/proxy/watch/rescan',
  devicesByDeviceIdProxyWatchSearch: '/api/devices/:deviceId/proxy/watch/search',
  devicesByDeviceIdProxyWebrtcQuality: '/api/devices/:deviceId/proxy/webrtc-quality',
} as const;

// ---------------------------------------------------------------------------
// Path builder — substitutes `:foo` segments in a pattern with values from
// `params`. Used by both server tests and any client code that needs a
// concrete URL from one of the patterns above (clients can't post to
// "/api/feedback/:id/comments" — they need "/api/feedback/abc/comments").
//
// Throws if a `:foo` placeholder has no matching params entry, so callers
// can't accidentally ship "/api/feedback/undefined/comments" URLs.
// ---------------------------------------------------------------------------

export function buildPath(pattern: string, params: Record<string, string | number> = {}): string {
  return pattern.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, name: string) => {
    if (!(name in params)) {
      throw new Error(`buildPath: missing param "${name}" for pattern "${pattern}"`);
    }
    return encodeURIComponent(String(params[name]));
  });
}
